import { Link as ButtonLink, Modal, Text } from '@devfolioco/genesis';
import { Analytics } from '@devfolioco/helpers';
import { connect } from 'react-redux';
import React from 'react';
import styled from 'styled-components';

import SignIn from './SignIn';
import SignUp from './SignUp';
import Verify from './Verify';
import Names from './Names';
import EditEmail from './EditEmail';

const VIEWS = {
  SIGNIN: 'SIGNIN',
  SIGNUP: 'SIGNUP',
  NAMES: 'NAMES',
  VERIFY: 'VERIFY',
  HIDE: 'HIDE',
  EDIT_EMAIL: 'EDIT_EMAIL',
};

const Flex = styled.div`
  display: flex;
  flex-direction: column;
  position: relative;
`;

const linkStyles = {
  color: '#fff',
  border: 'none',
  textDecoration: 'underline',
  fontSize: 18,
};

class RegisterWall extends React.PureComponent {
  constructor(props) {
    super(props);

    this.state = {
      view: props.initialView,
      currentEmail: props.email,
    };
  }

  componentDidMount() {
    const { isLoading } = this.props;

    if (!isLoading) {
      this.updateView();
    }
  }

  componentDidUpdate(prevProps) {
    const { userID, firstName, lastName, isAuthenticated, isEmailVerified, isLoading, email, event, eventAttributes } =
      this.props;

    const { view } = this.state;

    const {
      userID: prevUserID,
      firstName: prevFirstName,
      lastName: prevLastName,
      isAuthenticated: prevIsAuthenticated,
      isEmailVerified: prevIsEmailVerified,
      isLoading: prevIsLoading,
    } = prevProps;

    if (
      isLoading !== prevIsLoading ||
      userID !== prevUserID ||
      firstName !== prevFirstName ||
      lastName !== prevLastName ||
      isAuthenticated !== prevIsAuthenticated ||
      isEmailVerified !== prevIsEmailVerified
    ) {
      if (!isLoading) {
        this.updateView();
      }
    }

    if (isAuthenticated !== prevIsAuthenticated && isAuthenticated && typeof event === 'string') {
      Analytics.trackEvent(`${view === VIEWS.SIGNIN ? 'Sign in' : 'Sign up'} ${event}`, eventAttributes);
    }

    if (prevProps.email !== email) {
      this.updateCurrentEmail(email);
    }
  }

  getUpdatedViewState = () => {
    const { userID, firstName, lastName, isAuthenticated, isEmailVerified, requireEmailVerification, requireName } =
      this.props;

    const { view } = this.state;

    if (!isAuthenticated) {
      if (view === VIEWS.SIGNIN) {
        return view;
      }

      return VIEWS.SIGNUP;
    }

    // Until we have the userID fetched we'll keep the same signin view.
    if (view === VIEWS.SIGNIN && !userID) {
      return view;
    }

    if (requireName && (!firstName || !lastName)) {
      return VIEWS.NAMES;
    }

    if (requireEmailVerification && !isEmailVerified) {
      return VIEWS.VERIFY;
    }

    return VIEWS.HIDE;
  };

  updateViewState = view => this.setState({ view });

  updateView = () => {
    const view = this.getUpdatedViewState();
    const { view: viewState } = this.state;

    if (view !== viewState) {
      if (view === VIEWS.HIDE) {
        this.postAuthentication();
      }

      this.updateViewState(view);
    }
  };

  postAuthentication = () => {
    const { postAuthentication } = this.props;

    if (typeof postAuthentication === 'function') {
      postAuthentication();
    }
  };

  updateCurrentEmail = email => {
    this.setState({
      currentEmail: email,
    });
  };

  render() {
    const { view, currentEmail } = this.state;
    const {
      title,
      subtitle,
      disabled,
      preFields,
      postFields,
      closeOnEsc,
      closeOnClickOutside,
      requireName,
      onClose,
      mapTitleToView,
      mapSubtitleToView,
      loading,
    } = this.props;

    if (view === VIEWS.HIDE || loading) {
      return null;
    }

    return (
      <Modal width={560} onClose={onClose} closeOnEsc={closeOnEsc} closeOnClickOutside={closeOnClickOutside}>
        <Flex css="padding: 50px; width: 100%;">
          {(mapTitleToView[view] || title) && (
            <div css="margin-bottom: 1em;">
              <Text size={48} weight="black" upgrade style={{ lineHeight: '1.1' }}>
                {mapTitleToView[view] || title}
              </Text>
            </div>
          )}

          {(mapSubtitleToView[view] || subtitle) && (
            <div css="margin-bottom: 2em">
              <Text size={18} color="grey-7" upgrade>
                {mapSubtitleToView[view] || subtitle}
              </Text>
            </div>
          )}

          {view === VIEWS.SIGNUP && (
            <SignUp disabled={disabled} preFields={preFields} postFields={postFields} requireName={requireName} />
          )}

          {view === VIEWS.SIGNIN && <SignIn disabled={disabled} preFields={preFields} postFields={postFields} />}

          {view === VIEWS.NAMES && <Names />}

          {view === VIEWS.VERIFY && <Verify updateViewState={this.updateViewState} currentEmail={currentEmail} />}

          {view === VIEWS.EDIT_EMAIL && (
            <EditEmail
              updateViewState={this.updateViewState}
              currentEmail={currentEmail}
              updateCurrentEmail={this.updateCurrentEmail}
            />
          )}

          <Flex css="margin-top: 2em; position: absolute; bottom: -2.5em; left: 0">
            <Text>
              {view === VIEWS.SIGNIN && (
                <>
                  <Text color="white-0" size={18} weight="semibold" upgrade>
                    Don&apos;t have an account? Create one{' '}
                  </Text>
                  <ButtonLink
                    label="here"
                    style={linkStyles}
                    onClick={() => this.updateViewState(VIEWS.SIGNUP)}
                    disabled={view === VIEWS.SIGNUP}
                  />
                </>
              )}

              {view === VIEWS.SIGNUP && (
                <>
                  <Text color="white-0" size={18} weight="semibold" upgrade>
                    Already have an account?{' '}
                  </Text>
                  <ButtonLink
                    label="Log in"
                    style={linkStyles}
                    onClick={() => this.updateViewState(VIEWS.SIGNIN)}
                    disabled={view === VIEWS.SIGNIN}
                  />
                </>
              )}
            </Text>
          </Flex>
        </Flex>
      </Modal>
    );
  }
}

const mapStateToProps = ({ authentication, user }) => ({
  isAuthenticated: authentication.isAuthenticated,
  email: user.email,
  firstName: user.firstName,
  isEmailVerified: user.isEmailVerified,
  lastName: user.lastName,
  userID: user.userID,
  isLoading: user.isLoading || authentication.isLoading,
});

RegisterWall.defaultProps = {
  title: null,
  subtitle: null,
  initialView: VIEWS.HIDE,
  disabled: false,
  onClose: () => undefined,
  requireEmailVerification: false,
  closeOnClickOutside: false,
  closeOnEsc: false,
  requireName: false,
  mapTitleToView: {},
  mapSubtitleToView: {},
};

export default connect(mapStateToProps)(RegisterWall);
